import { Box, Typography, Container, Alert, Button, Stack, Divider } from '@mui/material';
import { ErrorOutline, Refresh, Info } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { CardSurface } from '../../ui/CardSurface';
import { ButtonPrimary } from '../../ui/ButtonPrimary';

export function ErrorFallback({ error, resetError }) {
  const isNetworkError = error?.message?.includes('Failed to fetch') || 
                         error?.message?.includes('Network Error') ||
                         error?.code === 'ERR_NETWORK';
  
  const getErrorMessage = () => {
    if (isNetworkError) {
      return 'Erro de conexão com o servidor. Verifique sua conexão e se a API está acessível.';
    }
    return error?.message || 'Erro desconhecido';
  };

  const getDetailedInfo = () => {
    if (isNetworkError) {
      return (
        <CardSurface sx={{ mt: 3, p: 2.5, bgcolor: 'info.light', border: '1px solid', borderColor: 'info.main' }}>
          <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Info sx={{ fontSize: 20, color: 'info.dark' }} />
              <Typography variant="subtitle2" fontWeight={600} color="info.dark">
                Possíveis causas:
              </Typography>
            </Box>
            <Box component="ul" sx={{ m: 0, pl: 3, color: 'info.dark' }}>
              <li>URL da API incorreta ou não acessível</li>
              <li>Problema de CORS (origem não permitida)</li>
              <li>Servidor da API não está rodando</li>
              <li>Certificado SSL inválido</li>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" color="info.dark" sx={{ fontSize: '0.75rem' }}>
              URL da API configurada: {import.meta.env.VITE_API_URL || 'Não configurada'}
            </Typography>
          </Stack>
        </CardSurface>
      );
    }
    return null;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        px: 2,
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <CardSurface sx={{ p: 4, textAlign: 'center' }}>
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
            >
              <ErrorOutline
                sx={{
                  fontSize: 80,
                  color: 'error.main',
                  mb: 3,
                }}
              />
            </motion.div>

            <Typography
              variant="h4"
              component="h1"
              fontWeight={700}
              gutterBottom
              sx={{ mb: 2 }}
            >
              Ops! Algo deu errado
            </Typography>

            <Alert severity="error" sx={{ mb: 3, textAlign: 'left', borderRadius: '12px' }}>
              <Typography variant="body1" fontWeight={500}>
                {getErrorMessage()}
              </Typography>
            </Alert>

            {getDetailedInfo()}

            {error?.stack && import.meta.env.DEV && (
              <CardSurface sx={{ mt: 3, p: 2.5, textAlign: 'left', bgcolor: 'grey.50' }}>
                <details>
                  <summary style={{ cursor: 'pointer', marginBottom: '10px', fontWeight: 600 }}>
                    Detalhes técnicos (dev)
                  </summary>
                  <Box
                    component="pre"
                    sx={{
                      background: 'rgba(0, 0, 0, 0.05)',
                      padding: 2,
                      borderRadius: '8px',
                      overflow: 'auto',
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      mt: 1,
                    }}
                  >
                    {error.stack}
                  </Box>
                </details>
              </CardSurface>
            )}

            <Box sx={{ mt: 4 }}>
              <ButtonPrimary
                onClick={resetError}
                size="large"
                startIcon={<Refresh />}
                sx={{
                  minWidth: 200,
                  py: 1.5,
                }}
              >
                Tentar Novamente
              </ButtonPrimary>
            </Box>
          </CardSurface>
        </motion.div>
      </Container>
    </Box>
  );
}
